import type {createBusinessRuntime} from '../../../packages/runtime/business-runtime.ts';
import type {PublishedProductCatalogSnapshot} from '../../../packages/catalog/product-catalog.ts';
import type {ServiceMode} from '../../../packages/contracts/operating-context.ts';
import type {ProductOptionSelectionInput} from '../../../packages/order/product-selection.ts';
import type {
  SmtP01WorkspaceMutationResult,
  SmtP01WorkspaceViewModel,
} from '../../../packages/runtime/smt-p01-workspace-contract.ts';
import type {
  MobileOrderingCartLineItem,
  MobileOrderingCategoryItem,
  MobileOrderingProductItem,
} from '@morefunos/ui';
import type {SmmActiveWorkPort} from './smm-active-work-port.ts';

type BusinessRuntime = ReturnType<typeof createBusinessRuntime>;
type NextId = (kind: 'line'|'intake'|'order'|'idempotency'|'review') => string;

type RuntimeProductProjection = NonNullable<ReturnType<BusinessRuntime['productProjection']>>;

export interface SmmOrderingOptionItem {
  readonly optionId: string;
  readonly label: string;
  readonly enabled: boolean;
  readonly priceDeltaLabel?: string;
}

export interface SmmOrderingOptionGroup {
  readonly optionGroupId: string;
  readonly label: string;
  readonly required: boolean;
  readonly min: number;
  readonly max: number;
  readonly options: readonly SmmOrderingOptionItem[];
}

export interface SmmOrderingConfiguration {
  readonly productId: string;
  readonly productName: string;
  readonly groups: readonly SmmOrderingOptionGroup[];
}

export interface SmmOrderingCartView {
  readonly countLabel: string;
  readonly totalLabel: string;
  readonly pricingStatus: SmtP01WorkspaceViewModel['pricing']['status'];
  readonly statusLabel?: string;
  readonly lines: readonly MobileOrderingCartLineItem[];
  readonly checkoutBlocked: boolean;
  readonly checkoutBlockingCodes: readonly string[];
}

export interface SmmOrderingComboProposalItem {
  readonly proposalId: string;
  readonly ruleLabel: string;
  readonly savingLabel: string;
  readonly resultingTotalLabel: string;
}

export interface SmmOrderingComboGroupItem {
  readonly groupId: string;
  readonly ruleId: string;
  readonly status: 'partial' | 'complete';
}

export interface SmmOrderingComboView {
  readonly proposals: readonly SmmOrderingComboProposalItem[];
  readonly groups: readonly SmmOrderingComboGroupItem[];
}

export interface SmmOrderingPresentationSnapshot {
  readonly catalogRevision: string;
  readonly categories: readonly MobileOrderingCategoryItem[];
  readonly products: readonly MobileOrderingProductItem[];
  readonly workspace: SmtP01WorkspaceViewModel | null;
  readonly serviceMode: ServiceMode | null;
  readonly cart: SmmOrderingCartView;
  readonly combo: SmmOrderingComboView;
}

export interface SmmOrderingPortConfig {
  readonly runtime: BusinessRuntime;
  readonly catalog: PublishedProductCatalogSnapshot;
  readonly activeWork: SmmActiveWorkPort;
  readonly operatorId: string;
  readonly deviceId: string;
  readonly sessionRef?: string;
  readonly businessDate: () => string;
  readonly now: () => string;
  readonly nextId: NextId;
}

const formatMoney = (currency: string, minor: number): string =>
  new Intl.NumberFormat('zh-HK', {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(minor / 100);

const statusForProduct = (projection: RuntimeProductProjection): Pick<MobileOrderingProductItem, 'statusLabel'|'statusTone'> => {
  if (!projection.visible) return {statusLabel: '未顯示', statusTone: 'neutral'};
  if (projection.availability === 'blocked') return {statusLabel: '暫停供應', statusTone: 'warning'};
  if (projection.availability === 'unbound') return {statusLabel: '供應未綁定', statusTone: 'warning'};
  if (projection.basePriceMinor === null) return {statusLabel: '價錢未綁定', statusTone: 'warning'};
  return {};
};

const disabledReasonFor = (projection: RuntimeProductProjection): string => {
  if (!projection.visible) return '目前不可顯示';
  if (projection.availability === 'blocked') return '目前暫停供應';
  if (projection.availability === 'unbound') return '供應狀態未綁定';
  if (projection.basePriceMinor === null) return '價錢未綁定';
  return '目前不可加入';
};

const actionForProduct = (projection: RuntimeProductProjection): MobileOrderingProductItem['action'] => {
  if (!projection.sellable) {
    return {kind: 'disabled', label: '不可加入', disabledReason: disabledReasonFor(projection)};
  }
  if (projection.requiresConfiguration || projection.hasOptions) {
    return {kind: 'configure', label: '選擇'};
  }
  return {kind: 'direct', label: '＋'};
};

const pricingDisplay = (workspace: SmtP01WorkspaceViewModel | null): Pick<SmmOrderingCartView, 'totalLabel'|'pricingStatus'|'statusLabel'> => {
  if (!workspace) return {totalLabel: '—', pricingStatus: 'empty'};
  const pricing = workspace.pricing;
  if (pricing.status === 'quoted' && pricing.currency && pricing.totalMinor !== undefined) {
    return {totalLabel: formatMoney(pricing.currency, pricing.totalMinor), pricingStatus: pricing.status, statusLabel: '已報價'};
  }
  if (pricing.status === 'pending_required') return {totalLabel: '待完成選項', pricingStatus: pricing.status, statusLabel: '必選未完成'};
  if (pricing.status === 'blocked') return {totalLabel: '暫不可報價', pricingStatus: pricing.status, statusLabel: '報價受阻'};
  if (pricing.status === 'unbound') return {totalLabel: '價錢未綁定', pricingStatus: pricing.status, statusLabel: '價錢未綁定'};
  if (pricing.status === 'rejected') return {totalLabel: '報價被拒', pricingStatus: pricing.status, statusLabel: '報價被拒'};
  return {totalLabel: '—', pricingStatus: pricing.status};
};

export function createSmmOrderingPort(config: SmmOrderingPortConfig) {
  const listeners = new Set<() => void>();

  const productProjection = (productId: string): RuntimeProductProjection | null =>
    config.runtime.productProjection(productId);

  const workspace = (): SmtP01WorkspaceViewModel | null => config.activeWork.currentWorkspace();

  const selectedOptionNames = (line: SmtP01WorkspaceViewModel['lines'][number]): string[] => {
    const projection = productProjection(line.productId);
    if (!projection) return [];
    const names: string[] = [];
    for (const selection of line.selections) {
      const group = projection.optionGroups.find((candidate) => candidate.optionGroupId === selection.optionGroupId);
      if (!group) continue;
      for (const optionId of selection.optionIds) {
        const option = group.options.find((candidate) => candidate.optionId === optionId);
        if (option) names.push(option.name);
      }
    }
    return names;
  };

  const buildSnapshot = (): SmmOrderingPresentationSnapshot => {
    const currentWorkspace = workspace();
    const activeCategories = [...config.catalog.payload.categories]
      .filter((category) => category.status === 'active')
      .sort((a, b) => a.order - b.order);

    const projectedProducts = config.catalog.payload.products
      .filter((entry) => entry.product.status === 'active' && entry.eligibleChannels.includes('store'))
      .map((entry) => ({entry, projection: productProjection(entry.product.id)}))
      .filter((item): item is {entry: typeof item.entry; projection: RuntimeProductProjection} => Boolean(item.projection?.visible));

    const categoryIdsWithProducts = new Set(projectedProducts.map((item) => item.entry.categoryId));
    const categories: MobileOrderingCategoryItem[] = activeCategories
      .filter((category) => categoryIdsWithProducts.has(category.categoryId))
      .map((category) => ({id: category.categoryId, label: category.names.operations || category.names.customer}));

    const quantityByProduct = new Map<string, number>();
    for (const line of currentWorkspace?.lines ?? []) {
      quantityByProduct.set(line.productId, (quantityByProduct.get(line.productId) ?? 0) + line.quantity);
    }

    const products: MobileOrderingProductItem[] = projectedProducts.map(({entry, projection}) => ({
      id: projection.productId,
      categoryId: entry.categoryId,
      name: projection.operationsName || projection.customerName,
      ...(entry.description ? {subtitle: entry.description} : {}),
      priceLabel: projection.basePriceMinor === null ? '—' : formatMoney(projection.currency, projection.basePriceMinor),
      ...statusForProduct(projection),
      ...(quantityByProduct.has(projection.productId) ? {quantityLabel: `購物車 ${quantityByProduct.get(projection.productId)}`} : {}),
      action: actionForProduct(projection),
    }));

    const count = (currentWorkspace?.lines ?? []).reduce((sum, line) => sum + line.quantity, 0);
    const pricing = pricingDisplay(currentWorkspace);
    const lines: MobileOrderingCartLineItem[] = (currentWorkspace?.lines ?? []).map((line) => {
      const detailParts = selectedOptionNames(line);
      if (line.note) detailParts.push(line.note);
      return {
        id: line.lineId,
        name: line.operationsName,
        ...(detailParts.length ? {detail: detailParts.join(' · ')} : {}),
        quantityLabel: String(line.quantity),
        canDecrease: line.quantity > 1,
        canIncrease: true,
        canRemove: true,
      };
    });

    const comboProposals = currentWorkspace
      ? config.runtime.smtP01Workspace.comboProposals(currentWorkspace.workspaceId)
      : [];
    const comboCurrency = currentWorkspace?.pricing.currency ?? 'HKD';

    return {
      catalogRevision: config.catalog.catalogRevision,
      categories,
      products,
      workspace: currentWorkspace,
      serviceMode: currentWorkspace?.serviceMode ?? null,
      cart: {
        countLabel: String(count),
        totalLabel: pricing.totalLabel,
        pricingStatus: pricing.pricingStatus,
        ...(pricing.statusLabel ? {statusLabel: pricing.statusLabel} : {}),
        lines,
        checkoutBlocked: currentWorkspace?.checkout.blocked ?? true,
        checkoutBlockingCodes: currentWorkspace?.checkout.blockingCodes ?? ['SMM_WORKSPACE_REQUIRED'],
      },
      combo: {
        proposals: comboProposals.map((proposal) => ({
          proposalId: proposal.proposalId,
          ruleLabel: proposal.groups.map((group) => group.ruleId).join('＋'),
          savingLabel: formatMoney(comboCurrency, proposal.savingMinor),
          resultingTotalLabel: formatMoney(comboCurrency, proposal.resultingTotalMinor),
        })),
        groups: (currentWorkspace?.comboGroups ?? []).map((group) => ({
          groupId: group.groupId,
          ruleId: group.ruleId,
          status: group.status,
        })),
      },
    };
  };

  let currentSnapshot = buildSnapshot();

  const publish = (): void => {
    currentSnapshot = buildSnapshot();
    for (const listener of listeners) listener();
  };

  const publishMutation = <T>(result: SmtP01WorkspaceMutationResult<T>): SmtP01WorkspaceMutationResult<T> => {
    publish();
    return result;
  };

  const directAdd = (productId: string): SmtP01WorkspaceMutationResult<void> => {
    const projection = productProjection(productId);
    if (!projection || actionForProduct(projection).kind !== 'direct') throw new Error('SMM_DIRECT_ADD_NOT_AVAILABLE');
    config.activeWork.ensureWorkspace();
    return publishMutation(config.runtime.smtP01Workspace.addProduct(config.activeWork.mutationContext(), {
      lineId: config.nextId('line'),
      productId,
      quantity: 1,
      selections: [],
    }));
  };

  const configuredAdd = (productId: string, selections: readonly ProductOptionSelectionInput[]): SmtP01WorkspaceMutationResult<void> => {
    const projection = productProjection(productId);
    if (!projection || actionForProduct(projection).kind !== 'configure') throw new Error('SMM_CONFIGURATION_NOT_AVAILABLE');
    config.activeWork.ensureWorkspace();
    return publishMutation(config.runtime.smtP01Workspace.addProduct(config.activeWork.mutationContext(), {
      lineId: config.nextId('line'),
      productId,
      quantity: 1,
      selections,
    }));
  };

  const changeQuantity = (lineId: string, delta: number): SmtP01WorkspaceMutationResult<void> => {
    const current = workspace();
    const line = current?.lines.find((candidate) => candidate.lineId === lineId);
    if (!current || !line) throw new Error('SMM_CART_LINE_NOT_FOUND');
    if (!Number.isSafeInteger(delta) || delta === 0) throw new Error('SMM_CART_QUANTITY_DELTA_INVALID');
    const quantity = line.quantity + delta;
    if (quantity <= 0) throw new Error('SMM_CART_USE_REMOVE_LINE');
    return publishMutation(config.runtime.smtP01Workspace.updateLine(config.activeWork.mutationContext(), {
      lineId,
      quantity,
      selections: line.selections,
      note: line.note,
    }));
  };

  const removeLine = (lineId: string): SmtP01WorkspaceMutationResult<void> => {
    const current = workspace();
    const line = current?.lines.find((candidate) => candidate.lineId === lineId);
    if (!current || !line) throw new Error('SMM_CART_LINE_NOT_FOUND');
    return publishMutation(config.runtime.smtP01Workspace.removeLine(config.activeWork.mutationContext(), {lineId}));
  };

  const confirmCombo = (proposalId: string): SmtP01WorkspaceMutationResult<void> => {
    const current = workspace();
    if (!current) throw new Error('SMM_WORKSPACE_REQUIRED');
    const proposal = config.runtime.smtP01Workspace
      .comboProposals(current.workspaceId)
      .find((candidate) => candidate.proposalId === proposalId);
    if (!proposal) throw new Error('SMM_COMBO_PROPOSAL_NOT_FOUND');
    return publishMutation(config.runtime.smtP01Workspace.confirmComboProposal(
      config.activeWork.mutationContext(),
      {proposalId},
    ));
  };

  const setServiceMode = (serviceMode: ServiceMode): SmtP01WorkspaceMutationResult<void> => {
    config.activeWork.ensureWorkspace();
    return publishMutation(config.runtime.smtP01Workspace.setServiceMode(config.activeWork.mutationContext(), {serviceMode}));
  };

  const configurationFor = (productId: string): SmmOrderingConfiguration | null => {
    const projection = productProjection(productId);
    if (!projection) return null;
    return {
      productId,
      productName: projection.operationsName || projection.customerName,
      groups: projection.optionGroups.map((group) => ({
        optionGroupId: group.optionGroupId,
        label: group.name,
        required: group.required,
        min: group.min,
        max: group.max,
        options: group.options.map((option) => ({
          optionId: option.optionId,
          label: option.name,
          enabled: option.enabled,
          ...(option.priceDeltaMinor !== null && option.priceDeltaMinor !== 0
            ? {priceDeltaLabel: formatMoney(projection.currency, option.priceDeltaMinor)}
            : {}),
        })),
      })),
    };
  };

  const finalize = async (): ReturnType<BusinessRuntime['placeOperatorActiveWorkspaceOrder']> => {
    const current = workspace();
    if (!current?.custody) throw new Error('SMM_ACTIVE_WORK_REQUIRED');
    if (current.checkout.blocked) throw new Error('SMM_CHECKOUT_BLOCKED');
    const result = await config.runtime.placeOperatorActiveWorkspaceOrder({
      workspaceId: current.workspaceId,
      expectedWorkspaceRevision: current.revision,
      expectedCustodyRevision: current.custody.custodyRevision,
      surface: 'smm',
      source: 'smm',
      deviceId: config.deviceId,
      operatorId: config.operatorId,
      businessDate: config.businessDate(),
      intakeId: config.nextId('intake'),
      orderId: config.nextId('order'),
      idempotencyKey: config.nextId('idempotency'),
      createdAt: config.now(),
      serviceMode: current.serviceMode,
      lineServiceModes: current.lineServiceModes,
      ...(config.sessionRef ? {sessionRef: config.sessionRef} : {}),
    });
    config.activeWork.clearAfterFinalization(current.workspaceId);
    publish();
    return result;
  };

  return {
    getSnapshot: (): SmmOrderingPresentationSnapshot => currentSnapshot,
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    refresh(): SmmOrderingPresentationSnapshot {
      publish();
      return currentSnapshot;
    },
    productProjection,
    configurationFor,
    directAdd,
    configuredAdd,
    changeQuantity,
    removeLine,
    confirmCombo,
    setServiceMode,
    finalize,
  };
}

export type SmmOrderingPort = ReturnType<typeof createSmmOrderingPort>;
